export const incidentAudienceEnum = {
    Customer: 'customer',
    Internal: 'internal',
} as const;

import type { IncidentFlowScope } from '@/incidents/forms/navigation';

export type IncidentAudience = (typeof incidentAudienceEnum)[keyof typeof incidentAudienceEnum];

export const defaultIncidentAudience: IncidentAudience = incidentAudienceEnum.Internal;

const systemRolePriority = ['admin', 'noc-admin', 'cdim-admin', 'employee', 'contractor', 'customer', 'visitor'];

/**
 * Customer incident intake should only be used when the highest-priority known
 * system role is explicitly `customer`. Internal/admin roles always win.
 */
export function resolveIncidentAudience(systemRoles: string[]): IncidentAudience {
    const highestPriorityRole = systemRolePriority.find((role) => systemRoles.includes(role));

    return highestPriorityRole === 'customer' ? incidentAudienceEnum.Customer : incidentAudienceEnum.Internal;
}

/**
 * Resolves the incident audience for the current incident flow scope. Admin/NOC
 * view (`scope === 'noc'`) always uses the internal audience, regardless of the
 * user's CP roles — a NOC operator who also holds a `customer` role on some org
 * must still get the internal (admin) create flow while in admin view. Only the
 * org/customer scope defers to the role-based resolution.
 */
export function resolveIncidentAudienceForScope(
    systemRoles: string[],
    scope: IncidentFlowScope,
): IncidentAudience {
    return scope === 'noc' ? incidentAudienceEnum.Internal : resolveIncidentAudience(systemRoles);
}
